import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import CtaUrlButtonForm from "./CtaUrlButtonForm";

const useStyles = makeStyles(theme => ({
  list: {
    maxHeight: 360,
    overflowY: "auto",
    paddingTop: 0,
  },
  empty: {
    padding: theme.spacing(2, 1),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  itemPrimary: {
    fontWeight: 500,
  },
  itemSecondary: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sendHint: {
    color: green[600],
    fontSize: 12,
    marginTop: 2,
  },
  actions: {
    display: "flex",
    alignItems: "center",
  },
  createButton: {
    marginRight: "auto",
  },
}));

const CtaUrlButtonsMenu = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);

  const loadButtons = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cta-url-buttons", {
        params: ticketId ? { ticketId } : undefined,
      });
      setButtons(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (open) {
      loadButtons();
    }
  }, [open, loadButtons]);

  const handleClose = () => {
    if (sendingId || formOpen) return;
    onClose();
  };

  const handleSend = async button => {
    if (!ticketId || sendingId) return;
    setSendingId(button.id);
    try {
      await api.post(`/messages/${ticketId}/cta-url`, {
        body: button.body,
        displayText: button.displayText,
        url: button.url,
        ...(button.header ? { header: button.header } : {}),
        ...(button.footer ? { footer: button.footer } : {}),
      });
      toast.success(i18n.t("messagesInput.ctaUrl.success"));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (event, button) => {
    event.stopPropagation();
    if (sendingId) return;
    const confirmed = window.confirm(
      i18n.t("messagesInput.ctaUrl.deleteConfirm", {
        name: button.name || button.displayText,
      })
    );
    if (!confirmed) return;

    try {
      await api.delete(`/cta-url-buttons/${button.id}`);
      setButtons(prev => prev.filter(item => item.id !== button.id));
      toast.success(i18n.t("messagesInput.ctaUrl.deleted"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleEdit = (event, button) => {
    event.stopPropagation();
    setEditingButton(button);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditingButton(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingButton(null);
  };

  const handleSaved = () => {
    loadButtons();
  };

  const snippet = text => {
    if (!text) return "";
    return text.length > 80 ? `${text.slice(0, 80)}…` : text;
  };

  return (
    <>
      <Dialog open={open && !formOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("messagesInput.ctaUrl.listTitle")}</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <div className={classes.empty}>
              <CircularProgress size={28} />
            </div>
          ) : buttons.length === 0 ? (
            <Typography className={classes.empty}>
              {i18n.t("messagesInput.ctaUrl.emptyList")}
            </Typography>
          ) : (
            <List className={classes.list} dense>
              {buttons.map(button => (
                <ListItem
                  key={button.id}
                  button
                  disabled={Boolean(sendingId)}
                  onClick={() => handleSend(button)}
                >
                  <ListItemText
                    primary={
                      <span className={classes.itemPrimary}>
                        {button.name || button.displayText}
                      </span>
                    }
                    secondary={
                      <>
                        <span className={classes.itemSecondary}>
                          {button.displayText} · {button.url}
                        </span>
                        {button.body ? (
                          <span className={classes.itemSecondary}>
                            {snippet(button.body)}
                          </span>
                        ) : null}
                        <span className={classes.sendHint}>
                          {sendingId === button.id
                            ? i18n.t("messagesInput.ctaUrl.sending")
                            : i18n.t("messagesInput.ctaUrl.clickToSend")}
                        </span>
                      </>
                    }
                  />
                  <ListItemSecondaryAction className={classes.actions}>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      disabled={Boolean(sendingId)}
                      onClick={event => handleEdit(event, button)}
                      title={i18n.t("messagesInput.ctaUrl.edit")}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      disabled={Boolean(sendingId)}
                      onClick={event => handleDelete(event, button)}
                      title={i18n.t("messagesInput.ctaUrl.delete")}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.createButton}
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={Boolean(sendingId)}
          >
            {i18n.t("messagesInput.ctaUrl.createNew")}
          </Button>
          <Button onClick={handleClose} disabled={Boolean(sendingId)} color="secondary">
            {i18n.t("messagesInput.ctaUrl.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <CtaUrlButtonForm
        open={formOpen}
        onClose={handleFormClose}
        ticketId={ticketId}
        initialButton={editingButton}
        onSaved={handleSaved}
        onSent={onClose}
      />
    </>
  );
};

export default CtaUrlButtonsMenu;
